import { ReactNode } from "react";
import { Loader2, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows?: T[];
  data?: T[];
  loading?: boolean;
  isLoading?: boolean;
  total?: number;
  page?: number;
  limit?: number;
  onPageChange?: (p: number) => void;
  onLimitChange?: (l: number) => void;
  rowKey?: (row: T) => string;
  emptyText?: string;
  actions?: (row: T) => ReactNode;
}

export function DataTable<T extends { id: string }>({
  columns,
  rows,
  data,
  loading,
  isLoading,
  total = 0,
  page = 1,
  limit = 10,
  onPageChange = () => {},
  onLimitChange = () => {},
  rowKey,
  emptyText = "No records found",
  actions,
}: DataTableProps<T>) {
  const actualRows = rows ?? data ?? [];
  const actualLoading = loading ?? isLoading ?? false;
  const safeLimit = Math.max(1, limit || 10);
  const safeTotal = Math.max(0, total || 0);
  const safePage = Math.max(1, page || 1);
  const totalPages = Math.max(1, Math.ceil(safeTotal / safeLimit));
  const showFrom = safeTotal === 0 ? 0 : (safePage - 1) * safeLimit + 1;
  const showTo = Math.min(safePage * safeLimit, safeTotal);

  return (
    <div className="rounded-xl border bg-card shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              {columns.map((c) => (
                <th key={c.key} className={cn("px-4 py-3 font-medium", c.className)}>
                  {c.header}
                </th>
              ))}
              {actions && <th className="px-4 py-3 font-medium text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="relative">
            {actualRows.length === 0 && actualLoading ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-16 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                </td>
              </tr>
            ) : actualRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Inbox className="h-8 w-8" />
                    <p>{emptyText}</p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {actualRows.map((row) => (
                  <tr
                    key={rowKey ? rowKey(row) : row.id}
                    className={cn(
                      "border-b transition hover:bg-muted/30 last:border-0",
                      actualLoading && "opacity-40"
                    )}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={cn("px-4 py-3 text-foreground", c.className)}>
                        {c.render ? c.render(row) : (row as any)[c.key]}
                      </td>
                    ))}
                    {actions && <td className="px-4 py-3 text-right">{actions(row)}</td>}
                  </tr>
                ))}
                {actualLoading && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-card/5 backdrop-blur-[0.5px]">
                    <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  </div>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            Showing {showFrom}-{showTo} of {safeTotal}
          </span>
          <Select value={String(safeLimit)} onValueChange={(v) => onLimitChange(Number(v))}>
            <SelectTrigger className="h-8 w-[88px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Pagination className="m-0 w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage > 1) onPageChange(safePage - 1);
                }}
                className={cn(safePage === 1 && "pointer-events-none opacity-50")}
                href="#"
              />
            </PaginationItem>
            {Array.from({ length: totalPages })
              .slice(0, 5)
              .map((_, i) => {
                const p = i + 1;
                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === safePage}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (safePage < totalPages) onPageChange(safePage + 1);
                }}
                className={cn(safePage === totalPages && "pointer-events-none opacity-50")}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}
